import React, {Component} from 'react';
import axios from 'axios';
import Chip from '@material-ui/core/Chip';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import MuiButton from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Auth from '../../../auth/auth';

// ponytail: self-contained widget (own axios + local state), matching its neighbour OwnershipPanel.
// dd-details-view is not connect()ed — it takes props from the connected parent — so the redux route
// would cost three files of plumbing for one POST.
const API = process.env.REACT_APP_API_URL || '';

const LABELS = {internal: 'Internal', confidential: 'Confidential'};

function authHeaders() {
  return {'Accept': 'application/json', 'Content-Type': 'application/json', 'x-access-token': new Auth().getAccessToken()};
}

// The warning changes with the DIRECTION, because the two directions are opposite harms and a single
// generic sentence would be ignored inside a week.
function consequenceOf(next) {
  return next === 'confidential' ?
    'Once approved, anyone not cleared for confidential data stops seeing this — its columns, its lineage and its change history included.' :
    'Once approved, this becomes visible to everyone with access to the schema, including people who cannot see confidential data today.';
}

class ClassificationPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      proposed: '',
      reason: '',
      submitting: false,
      error: '',
      raisedRequestId: '',
      // Phase 1 residual — the confirm action's own two bits of state.
      confirming: false,
      confirmed: false,
    };
  }

  // The drawer swaps selectedItem in place without unmounting (dd-details-view openDrawer), so React
  // reuses this instance across objects. Without this reset, proposing on A and then clicking B shows
  // B's chip under A's green "Proposal raised and waiting for approval" — a false statement about a
  // visibility control, on the entity the user is now looking at. Same guard, same reason, as
  // OwnershipPanel.componentDidUpdate.
  componentDidUpdate(prevProps) {
    if (prevProps.entityId !== this.props.entityId || prevProps.entityType !== this.props.entityType) {
      // `confirming`/`confirmed` reset with the rest, and for the identical reason: without it,
      // confirming A then clicking B shows B's chip under A's green "Classification confirmed" — a
      // false statement about a visibility control, on the entity the user is now looking at.
      this.setState({open: false, proposed: '', reason: '', submitting: false, error: '',
        raisedRequestId: '', confirming: false, confirmed: false});
    }
  }

  // Affirms the CURRENT value. Sends `expected` — what the panel is displaying, which IS what the
  // user is looking at, so this is a genuine optimistic check rather than ceremony. The server puts
  // it in the UPDATE's WHERE clause, so a concurrent reclassify between render and click stamps
  // nothing and returns a refusal instead of recording a review of the wrong value.
  confirmCurrent = () => {
    const {entityType, entityId, classification} = this.props;
    const path = entityType === 'OBJECT' ? 'object' : 'schema';
    this.setState({confirming: true, error: '', confirmed: false});
    axios.post(`${API}/dataDictionary/${path}/${entityId}/classification/confirm`,
        {expected: classification}, {headers: authHeaders()})
        .then((res) => {
          const body = res.data || {};
          if (!body.status) {
            // The 200/{status:false} refusal shape this codebase uses. Surfacing `body.error`
            // verbatim matters here: the stale case tells the user to reload, which is actionable,
            // and inventing a generic message would lose that.
            return this.setState({confirming: false, error: body.error || 'Could not confirm'});
          }
          this.setState({confirming: false, confirmed: true});
          if (this.props.onConfirmed) this.props.onConfirmed();
        })
        .catch((err) => this.setState({confirming: false,
          error: String((err && err.message) || 'Could not confirm')}));
  }

  openDialog = () => {
    // Two values, so the dialog opens on the one the entity is not.
    const other = this.props.classification === 'confidential' ? 'internal' : 'confidential';
    this.setState({open: true, proposed: other, reason: '', error: '', raisedRequestId: ''});
  };

  closeDialog = () => this.setState({open: false});

  handleChange = (field) => (event) => this.setState({[field]: event.target.value, error: ''});

  submit = () => {
    const {entityType, entityId} = this.props;
    const segment = entityType === 'SCHEMA' ? 'schema' : 'object';
    this.setState({submitting: true, error: ''});
    axios.post(`${API}/dataDictionary/${segment}/${entityId}/classification`,
        {classification: this.state.proposed, justification: this.state.reason.trim()},
        {headers: authHeaders()},
    ).then((res) => {
      const body = res.data || {};
      if (!body.status) {
        return this.setState({submitting: false, error: body.error || 'The proposal was not accepted'});
      }
      this.setState({submitting: false, open: false, raisedRequestId: (body.response || {}).requestId || ''});
      if (this.props.onProposed) this.props.onProposed();
    }).catch((err) => {
      this.setState({submitting: false, error: String((err.response && err.response.data && err.response.data.error) || err.message || err)});
    });
  };

  render() {
    const {classification, entityId, classifiedAt, classifiedBy, classifiedByName} = this.props;
    // Absent, not defaulted. transformSchema/transformObject deliberately emit no fallback: a chip
    // reading "Internal" for a value that was never selected would be a confident answer about the
    // one field that decides who can see this row.
    if (!classification) return null;

    const {open, proposed, reason, submitting, error, raisedRequestId, confirming, confirmed} = this.state;
    const canSubmit = Boolean(reason.trim()) && Boolean(proposed) && !submitting;

    return (
      <div className="classification-panel" data-testid="dd-classification-panel" data-entity-id={entityId}>
        <div style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10}}>
          <Chip
            size="small"
            label={LABELS[classification] || classification}
            data-testid="dd-classification-chip"
            data-entity-id={entityId}
            data-classification={classification}
            style={classification === 'confidential' ?
              {backgroundColor: '#fbeee2', color: '#8a4210', fontWeight: 600} :
              {backgroundColor: 'transparent', color: '#4b5e68', border: '1px solid #b9c8d0', fontWeight: 600}}
          />
          <button
            type="button"
            className="link-button"
            onClick={this.openDialog}
            data-testid="dd-classification-propose"
            data-entity-id={entityId}
            style={{background: 'none', border: 0, padding: 0, color: '#026684', fontWeight: 500, cursor: 'pointer'}}
          >
            Propose change
          </button>
          {/* PHASE 1 RESIDUAL — the button that did not exist, which is why the coverage figure was
              an upper bound. `openDialog` pre-selects the OTHER value and both MenuItems disable the
              current one, so a reviewer who concludes "this is correctly classified" previously had
              no way to say so: `proposeClassificationChange` refuses a same-value proposal outright.
              "Reviewed and confirmed" was byte-identical to "never opened".
              Labelled from the CURRENT value, because it affirms rather than changes. */}
          <button
            type="button"
            className="link-button"
            onClick={this.confirmCurrent}
            disabled={confirming}
            data-testid="dd-classification-confirm"
            data-entity-id={entityId}
            style={{background: 'none', border: 0, padding: 0, color: '#026684', fontWeight: 500,
              cursor: confirming ? 'default' : 'pointer', opacity: confirming ? 0.6 : 1}}
          >
            {confirming ? 'Confirming…' : `Confirm ${LABELS[classification] || classification}`}
          </button>
        </div>

        {/* The decision on record. "No classification decision on record" is a claim about the
            RECORD, not about the value — the chip above already states the value. Rendering the raw
            id when the name does not resolve is deliberate: a departed reviewer still reviewed it,
            and "reviewed by someone we can no longer name" is true while "nobody" would not be. */}
        <div style={{fontSize: 12, color: '#9e9e9e', marginTop: 6}} data-testid="dd-classification-reviewed">
          {classifiedAt ?
            `Last reviewed by ${classifiedByName || classifiedBy || 'an unknown user'} on ${new Date(classifiedAt).toLocaleDateString()}` :
            'No classification decision on record'}
        </div>

        {this.props.showSaveNote && (
          <div style={{fontSize: 12, color: '#9e9e9e', marginTop: 6}} data-testid="dd-classification-note">
            Goes through Change Control. <b>Save Changes</b> does not apply it.
          </div>
        )}

        {confirmed && (
          <div style={{fontSize: 12, color: '#2e7d32', marginTop: 6}} data-testid="dd-classification-confirmed">
            Classification confirmed.
          </div>
        )}

        {raisedRequestId && (
          <div style={{fontSize: 12, color: '#2e7d32', marginTop: 6}} data-testid="dd-classification-raised">
            Proposal raised and waiting for approval.
          </div>
        )}

        <Dialog open={open} onClose={this.closeDialog} maxWidth="sm" fullWidth
          PaperProps={{'data-testid': 'dd-classification-dialog'}}>
          <DialogTitle>Propose a reclassification</DialogTitle>
          <DialogContent>
            <div style={{fontSize: 13, color: '#606060', marginBottom: 16}}>
              {this.props.label || entityId}
            </div>

            <div style={{marginBottom: 16}}>
              <div style={{fontSize: 12, color: '#606060', marginBottom: 4}}>Current</div>
              <Chip size="small" label={LABELS[classification] || classification} />
            </div>

            <FormControl fullWidth style={{marginBottom: 16}}>
              <div style={{fontSize: 12, color: '#606060', marginBottom: 4}}>New classification</div>
              <Select
                value={proposed}
                onChange={this.handleChange('proposed')}
                inputProps={{'data-testid': 'dd-classification-select'}}
              >
                <MenuItem value="internal" disabled={classification === 'internal'}>Internal</MenuItem>
                <MenuItem value="confidential" disabled={classification === 'confidential'}>Confidential</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Why this change is needed"
              value={reason}
              onChange={this.handleChange('reason')}
              multiline
              // minRows, not rows: MUI 4.12 deprecated `rows` on multiline inputs. The older prop
              // is still used elsewhere in this repo; a new file need not inherit the warning.
              minRows={3}
              fullWidth
              required
              variant="outlined"
              helperText="Stored on the request and shown to the approver."
              inputProps={{'maxLength': 500, 'data-testid': 'dd-classification-reason'}}
            />

            <div style={{
              marginTop: 16, padding: '12px 14px', borderRadius: 4,
              background: '#fdf3ec', border: '1px solid #e0bb96', color: '#8a4210', fontSize: 13,
            }} data-testid="dd-classification-consequence">
              {consequenceOf(proposed)}
            </div>

            {error && (
              <div style={{color: '#c62828', fontSize: 13, marginTop: 12}} data-testid="dd-classification-error">
                {error}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <MuiButton onClick={this.closeDialog} data-testid="dd-classification-cancel">Cancel</MuiButton>
            <MuiButton
              onClick={this.submit}
              color="primary"
              variant="contained"
              disabled={!canSubmit}
              data-testid="dd-classification-submit"
            >
              Submit for approval
            </MuiButton>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default ClassificationPanel;
